import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import ReactMarkdown from 'react-markdown';

interface ChatMessage {
  type: 'user' | 'bot';
  message: string;
  isTyping?: boolean;
}

const ChatSection = () => {
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([
    {type: 'bot', message: '안녕하세요! 무엇을 도와드릴까요?'}
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [fullBotResponse, setFullBotResponse] = useState('');
  const [currentTypingIndex, setCurrentTypingIndex] = useState(-1);
  const [displayedMessage, setDisplayedMessage] = useState('');
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const typingSpeedRef = useRef(30); // ms per character
  const loadingDotsRef = useRef(0);
  const loadingAnimationRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollElement = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  }, [chatHistory, displayedMessage]);

  useEffect(() => {
    if (currentTypingIndex >= 0 && fullBotResponse) {
      if (displayedMessage.length < fullBotResponse.length) {
        const timer = setTimeout(() => {
          setDisplayedMessage(fullBotResponse.substring(0, displayedMessage.length + 1));
        }, typingSpeedRef.current);
        return () => clearTimeout(timer);
      } else {
        setChatHistory(prev => {
          const newHistory = [...prev];
          if (currentTypingIndex < newHistory.length) {
            newHistory[currentTypingIndex] = {
              ...newHistory[currentTypingIndex],
              message: fullBotResponse,
              isTyping: false
            };
          }
          return newHistory;
        });
        setCurrentTypingIndex(-1);
        setFullBotResponse('');
        setDisplayedMessage('');
      }
    }
  }, [currentTypingIndex, fullBotResponse, displayedMessage]);

  useEffect(() => {
    if (isLoading) {
      if (loadingAnimationRef.current === null) {
        loadingAnimationRef.current = setInterval(() => {
          loadingDotsRef.current = (loadingDotsRef.current + 1) % 4;
          setChatHistory(prev => [...prev]);
        }, 300);
      }
    } else {
      if (loadingAnimationRef.current !== null) {
        clearInterval(loadingAnimationRef.current);
        loadingAnimationRef.current = null;
      }
    }
    
    return () => {
      if (loadingAnimationRef.current !== null) {
        clearInterval(loadingAnimationRef.current);
      }
    };
  }, [isLoading]);

  const getLoadingDots = () => {
    return '.'.repeat(loadingDotsRef.current + 1);
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (chatMessage.trim() === '' || isLoading) return;

    const userMessage = chatMessage.trim();
    setChatHistory(prev => [...prev, {type: 'user', message: userMessage}]);
    setChatMessage('');
    setIsLoading(true);
    
    try {
      const response = await fetch('https://ilwoonge.app.n8n.cloud/webhook/cb024b82-66c3-44d6-a79f-dde6bdbab899', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: userMessage }),
      });
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      const textResponse = await response.text();
      
      setChatHistory(prev => [
        ...prev, 
        {
          type: 'bot', 
          message: '', 
          isTyping: true
        }
      ]);
      
      setFullBotResponse(textResponse || '응답을 찾을 수 없습니다. 다시 시도해 주세요.');
      setDisplayedMessage('');
      setCurrentTypingIndex(chatHistory.length + 1);
    } catch (error) {
      console.error('Error calling webhook:', error);
      setChatHistory(prev => [...prev, {
        type: 'bot', 
        message: '죄송합니다. 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  return (
    <Card className="mb-6">
      <CardContent className="p-6">
        <div className="space-y-4">
          <ScrollArea 
            className="border rounded-lg h-[10.24rem] max-h-[10.24rem]"
            ref={scrollAreaRef}
          >
            <div>
              {chatHistory.map((chat, index) => (
                <div key={index} className={`flex ${chat.type === 'user' ? 'justify-end' : 'justify-start'} mb-2 px-3 ${index === 0 ? 'pt-3' : ''} ${index === chatHistory.length - 1 ? 'pb-3' : ''}`}>
                  <div 
                    className={`max-w-[80%] p-3 rounded-lg ${
                      chat.type === 'user' 
                        ? 'bg-primary text-white' 
                        : 'bg-secondary'
                    }`}
                  >
                    {chat.type === 'user' ? (
                      <p className="text-sm">
                        {index === currentTypingIndex ? displayedMessage : chat.message}
                      </p>
                    ) : (
                      <div className="text-sm markdown-content">
                        {index === currentTypingIndex ? (
                          <ReactMarkdown>{displayedMessage}</ReactMarkdown>
                        ) : (
                          <ReactMarkdown>{chat.message}</ReactMarkdown>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {isLoading && currentTypingIndex === -1 && (
                <div className="flex justify-start mb-2 px-3 pb-3">
                  <div className="max-w-[80%] p-3 rounded-lg bg-secondary">
                    <p className="text-sm flex items-center">
                      <span>생각 중</span>
                      <span className="ml-1 inline-block">{getLoadingDots()}</span>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
          
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <Textarea 
              placeholder="메시지를 입력하세요..." 
              className="resize-none min-h-[40px] h-10"
              value={chatMessage}
              onChange={(e) => setChatMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
            />
            <Button type="submit" size="icon" disabled={isLoading}>
              <Send size={18} />
            </Button>
          </form>
        </div>
      </CardContent>
    </Card>
  );
};

export default ChatSection;
